import React, { useMemo, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import { normalizeAssetId } from '../tracking/trackingModel';
import { isLegacyBundleAsset } from '../tracking/deploymentUsageModel';
import './deployment-gear-editor.css';

function sortAssets(left, right) {
  return String(left?.category || '').localeCompare(String(right?.category || ''))
    || String(left?.name || '').localeCompare(String(right?.name || ''));
}

export default function DeploymentGearEditor({ deployment, state, onClose }) {
  const [addIds, setAddIds] = useState([]);
  const [removeIds, setRemoveIds] = useState([]);
  const [query, setQuery] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  const assignedIds = useMemo(() => new Set((deployment?.assetIds || []).map(normalizeAssetId)), [deployment]);
  const physicalAssets = useMemo(() => (state?.assets || [])
    .filter(asset => !isLegacyBundleAsset(asset)), [state?.assets]);

  const assigned = useMemo(() => physicalAssets
    .filter(asset => assignedIds.has(normalizeAssetId(asset.id)))
    .sort(sortAssets), [assignedIds, physicalAssets]);

  const available = useMemo(() => physicalAssets
    .filter(asset => asset.active !== false)
    .filter(asset => !assignedIds.has(normalizeAssetId(asset.id)))
    .filter(asset => !asset.currentDeploymentId && asset.status === 'available' && asset.condition === 'ready')
    .sort(sortAssets), [assignedIds, physicalAssets]);

  const search = String(query || '').trim().toLowerCase();
  const visibleAvailable = useMemo(() => !search ? available : available.filter(asset => {
    const id = normalizeAssetId(asset.id).toLowerCase();
    return id.includes(search)
      || String(asset.name || '').toLowerCase().includes(search)
      || String(asset.category || '').toLowerCase().includes(search);
  }), [available, search]);

  const toggle = (setter, id) => setter(current => current.includes(id)
    ? current.filter(item => item !== id)
    : [...current, id]);

  const save = async () => {
    if (!addIds.length && !removeIds.length) {
      setError('Choose at least one asset to add or remove.');
      return;
    }
    const changeSummary = [
      removeIds.length ? `Remove ${removeIds.length}` : '',
      addIds.length ? `add ${addIds.length}` : '',
    ].filter(Boolean).join(' and ');
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm(`${changeSummary} gear assignment${addIds.length + removeIds.length === 1 ? '' : 's'} for ${deployment.person}?`);
      if (!confirmed) return;
    }

    setSaving(true);
    setError('');
    setSaved('');
    try {
      const token = await getMemberAccessToken();
      if (!token) throw new Error('Member sign in required.');
      const response = await fetch('/api/admin-deployment-gear', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          deploymentId: deployment.id,
          addAssetIds: addIds,
          removeAssetIds: removeIds,
          reason,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to save gear assignment changes.');
      setSaved(payload?.edit?.requirementWarning
        ? `Saved. ${payload.edit.requirementWarning}`
        : 'Gear assignments updated. Refreshing live tracking…');
      window.setTimeout(() => window.location.reload(), 750);
    } catch (saveError) {
      setError(saveError?.message || 'Unable to save gear assignment changes.');
      setSaving(false);
    }
  };

  return (
    <section className="admin-card deployment-gear-editor">
      <div className="admin-section-head">
        <div>
          <h2>Edit Deployed Gear</h2>
          <p><strong>{deployment.person}</strong> · {deployment.incident} · {deployment.deploymentType || deployment.role || 'EMPF'}</p>
        </div>
        <button type="button" className="deployment-gear-close" onClick={onClose} disabled={saving}>Close</button>
      </div>

      <div className="deployment-gear-notice">
        This is an administrative assignment correction. Removed gear is released as <strong>Available / Ready</strong>. If gear was physically returned damaged or needs restock, use the normal Return workflow instead.
      </div>

      {error && <div className="tracking-error">{error}</div>}
      {saved && <div className="tracking-success">{saved}</div>}

      <div className="deployment-gear-columns">
        <div>
          <h3>Currently Assigned</h3>
          {!assigned.length && <div className="admin-empty">No individual physical gear is recorded on this deployment.</div>}
          <div className="deployment-gear-list">
            {assigned.map(asset => {
              const id = normalizeAssetId(asset.id);
              const selected = removeIds.includes(id);
              return (
                <label className={`deployment-gear-row ${selected ? 'is-remove' : ''}`} key={id}>
                  <input type="checkbox" checked={selected} onChange={() => toggle(setRemoveIds, id)} disabled={saving} />
                  <span><strong>{asset.name}</strong><small>{id} · {asset.category}</small></span>
                  <b>{selected ? 'REMOVE' : 'ASSIGNED'}</b>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <div className="deployment-gear-available-head">
            <h3>Available to Add</h3>
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search gear"
              disabled={saving}
            />
          </div>
          {!visibleAvailable.length && <div className="admin-empty">No matching available/ready gear.</div>}
          <div className="deployment-gear-list">
            {visibleAvailable.map(asset => {
              const id = normalizeAssetId(asset.id);
              const selected = addIds.includes(id);
              return (
                <label className={`deployment-gear-row ${selected ? 'is-add' : ''}`} key={id}>
                  <input type="checkbox" checked={selected} onChange={() => toggle(setAddIds, id)} disabled={saving} />
                  <span><strong>{asset.name}</strong><small>{id} · {asset.category}</small></span>
                  <b>{selected ? 'ADD' : 'READY'}</b>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <label className="deployment-gear-reason">
        <span>Admin note <small>(optional)</small></span>
        <input
          value={reason}
          onChange={event => setReason(event.target.value)}
          maxLength={300}
          placeholder="Example: clamshell was selected by mistake"
          disabled={saving}
        />
      </label>

      <div className="deployment-gear-footer">
        <span>{removeIds.length} remove · {addIds.length} add</span>
        <button type="button" onClick={save} disabled={saving || (!addIds.length && !removeIds.length)}>
          {saving ? 'Saving…' : 'Save Gear Assignments'}
        </button>
      </div>
    </section>
  );
}
